import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { MapPin, GraduationCap, Briefcase, Shield } from "lucide-react";
import InfoTip from "@/components/InfoTip";

export default function CandidateDetailModal({ result, open, onClose }) {
  if (!result) return null;
  const raw = result.candidate || {};
  // PRIVACY GUARD: Only safe de-identified fields
  const c = {
    anonymous_id: raw.anonymous_id,
    current_title: raw.current_title,
    current_employer_category: raw.current_employer_category,
    functional_area: raw.functional_area,
    therapeutic_areas: raw.therapeutic_areas,
    seniority_level: raw.seniority_level,
    highest_degree: raw.highest_degree,
    geography_region: raw.geography_region,
    ai_experience_summary: raw.ai_experience_summary,
    intent_level10_summary: raw.intent_level10_summary,
    fa_confidence: raw.fa_confidence,
    ta_confidence: raw.ta_confidence,
    remote_willing: raw.remote_willing,
  };
  const tas = (c.therapeutic_areas || []).filter(t => t && t !== "Unknown");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-heading text-lg text-slate-900">{c.current_title || "Candidate Profile"}</span>
            <span className="text-xs font-mono text-slate-400">{result.candidate_anonymous_id}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-xs text-slate-400">
            <Shield className="w-3 h-3" /> Anonymized profile -- candidate identity is protected
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Score Summary */}
          <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
                  result.composite_score >= 75 ? "bg-lime-100 text-lime-800" :
                  result.composite_score >= 50 ? "bg-teal-50 text-teal-800" :
                  "bg-slate-100 text-slate-600"
                }`}>
                  <span className="text-2xl font-accent font-bold">{result.composite_score}</span>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1 block">Composite</span>
              </div>
              <div className="flex-1 space-y-2.5">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      Experience Fit
                      <InfoTip text="Structured score based on seniority, functional area, therapeutic area overlap, and degree match against your search criteria." side="top" />
                    </span>
                    <span className="text-xs font-mono font-bold text-slate-700">{result.experience_fit_score}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2"><div className="bg-evergreen-950 rounded-full h-2 transition-all" style={{ width: `${result.experience_fit_score}%` }} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      Intent Alignment
                      <InfoTip text="AI-scored alignment between what your role offers and what this candidate's career trajectory suggests they want. This uses Varyynt's proprietary intent data." side="top" />
                    </span>
                    <span className="text-xs font-mono font-bold text-slate-700">{result.intent_alignment_score}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2"><div className="bg-lime-600 rounded-full h-2 transition-all" style={{ width: `${result.intent_alignment_score}%` }} /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {c.seniority_level && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Seniority</span>
                <span className="text-sm text-slate-900 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-slate-400" />{c.seniority_level}</span>
              </div>
            )}
            {c.functional_area && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Functional Area</span>
                <span className="text-sm text-slate-900">{c.functional_area}</span>
                {c.fa_confidence && <span className="text-[10px] text-slate-400 ml-1">({c.fa_confidence} confidence)</span>}
              </div>
            )}
            {c.current_employer_category && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Employer Type</span>
                <span className="text-sm text-slate-900">{c.current_employer_category}</span>
              </div>
            )}
            {c.geography_region && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Geography</span>
                <span className="text-sm text-slate-900 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{c.geography_region}</span>
              </div>
            )}
            {c.highest_degree && c.highest_degree !== "None" && c.highest_degree !== "null" && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Highest Degree</span>
                <span className="text-sm text-slate-900 flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5 text-slate-400" />{c.highest_degree}</span>
              </div>
            )}
            {c.remote_willing && (
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-0.5">Remote</span>
                <span className="text-sm text-lime-700 font-medium">Open to remote</span>
              </div>
            )}
          </div>

          {/* Therapeutic Areas */}
          {tas.length > 0 && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-2">Therapeutic Areas</span>
              <div className="flex flex-wrap gap-1.5">
                {tas.map(ta => <Badge key={ta} className="text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2.5 py-0.5">{ta}</Badge>)}
              </div>
              {c.ta_confidence && <span className="text-[10px] text-slate-400 mt-1 block">{c.ta_confidence} confidence</span>}
            </div>
          )}

          <Separator />

          {/* AI Experience Summary */}
          {c.ai_experience_summary && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                Career Summary
                <InfoTip text="An AI-generated anonymized summary of this candidate's career trajectory. No employer names or identifying details are included." side="top" />
              </span>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">{c.ai_experience_summary}</p>
            </div>
          )}

          {/* Intent Profile */}
          {c.intent_level10_summary && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                Intent Profile
                <InfoTip text="Inferred from career trajectory and behavioral signals -- not self-reported. Reflects what the candidate's career choices suggest they want based on patterns in their work history." side="top" />
              </span>
              <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 mt-1">
                <p className="text-sm text-lime-900 leading-relaxed">{c.intent_level10_summary}</p>
              </div>
            </div>
          )}

          {/* AI Rationale */}
          {result.ai_match_rationale && (
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-1">AI Match Rationale</span>
              <p className="text-sm text-slate-600 italic">{result.ai_match_rationale}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
